import React, { useEffect, useState, useCallback } from 'react';

export default function BlogPostsList({ API, adminToken, isDarkMode, showNotification, onEdit, onNew }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/blog/admin/posts`, { headers });
      if (res.ok) setPosts(await res.json());
      else showNotification?.(`Load failed: ${res.status}`, 'error');
    } catch (e) {
      showNotification?.(`Load error: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [API, adminToken]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const deletePost = async (slug) => {
    if (!window.confirm(`Delete post "${slug}"? This cannot be undone.`)) return;
    const res = await fetch(`${API}/blog/admin/posts/${slug}`, { method: 'DELETE', headers });
    if (res.ok) { showNotification?.('Post deleted', 'success'); load(); }
    else { showNotification?.(`Delete failed: ${res.status}`, 'error'); }
  };

  const togglePublish = async (post) => {
    const want = !post.is_published;
    const res = await fetch(`${API}/blog/admin/posts/${post.slug}`, {
      method: 'PUT',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_published: want }),
    });
    if (res.ok) { showNotification?.(want ? 'Published' : 'Unpublished', 'success'); load(); }
    else { showNotification?.(`Update failed: ${res.status}`, 'error'); }
  };

  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const rowBg = isDarkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50';
  const cardBg = isDarkMode ? 'bg-gray-900/40' : 'bg-gray-50';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold">All posts</h2>
          <p className={`text-sm mt-0.5 ${muted}`}>{posts.length} total · click to edit</p>
        </div>
        <button
          onClick={onNew}
          className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold"
        >
          + New post
        </button>
      </div>

      {loading ? (
        <div className={`text-center py-10 ${muted}`}>Loading…</div>
      ) : posts.length === 0 ? (
        <div className={`text-center py-12 rounded-lg ${cardBg} border ${border}`}>
          <div className="text-4xl mb-3">✈</div>
          <p className="text-base font-semibold mb-2">No flights scheduled yet</p>
          <p className={`text-sm ${muted} mb-4`}>Create your first post to get the blog flying.</p>
          <button onClick={onNew} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-bold">
            + Create first post
          </button>
        </div>
      ) : (
        <div className={`rounded-lg border ${border} overflow-hidden`}>
          <table className="w-full text-sm">
            <thead className={isDarkMode ? 'bg-gray-900/60' : 'bg-gray-100'}>
              <tr className="text-left">
                <th className="px-4 py-3 font-bold">Title</th>
                <th className="px-4 py-3 font-bold">Route</th>
                <th className="px-4 py-3 font-bold">Category</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold">Views</th>
                <th className="px-4 py-3 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(p => (
                <tr key={p.slug} className={`border-t ${border} ${rowBg}`}>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onEdit(p.slug)}
                      className="text-left font-semibold hover:text-blue-500"
                    >
                      {p.title || <em className={muted}>(untitled)</em>}
                    </button>
                    <div className={`text-xs ${muted} mt-0.5 font-mono`}>/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">
                    {p.from_city || '—'} → {p.to_city || '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">{p.category_slug || <em className={muted}>—</em>}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => togglePublish(p)}
                      className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        p.is_published
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {p.is_published ? '● Published' : '○ Draft'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs">{p.view_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <a
                      href={`/blog/${p.slug}${p.is_published ? '' : '?preview=1'}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-500 hover:underline mr-3"
                    >
                      ↗ View
                    </a>
                    <button
                      onClick={() => onEdit(p.slug)}
                      className="text-xs text-blue-500 hover:underline mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deletePost(p.slug)}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
